import { useEffect, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Grid2,
	MenuItem,
	TextField,
} from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	COMPACT_DIALOG_SX,
	COMPACT_OPTION_SX,
	EDITABLE_FIELD_SX,
} from "@/pages/reports/rollshop-operations/components/compactDialog";
import { ReadOnlyField } from "@/pages/reports/rollshop-operations/components/ReadOnlyField";
import { usePassIds } from "../hooks/useBarrels";
import type { BarrelLayout } from "../models/Barrel";

const tonnage = (value: number | null | undefined) =>
	value != null
		? value.toLocaleString("en-US", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})
		: null;

interface Props {
	open: boolean;
	/** The pass being edited; null adds a new one at the end. */
	layout: BarrelLayout | null;
	standId: number | null;
	passCount: number;
	isSaving: boolean;
	error: string;
	onClose: () => void;
	onSave: (passId: number, position: number) => void;
}

export const BarrelLayoutModal = ({
	open,
	layout,
	standId,
	passCount,
	isSaving,
	error,
	onClose,
	onSave,
}: Props) => {
	const [passId, setPassId] = useState<number | null>(null);
	const [position, setPosition] = useState(1);

	const { data: passIds = [], isLoading } = usePassIds({
		standId,
		enabled: open && standId !== null,
	});

	useEffect(() => {
		if (!open) return;
		setPassId(layout?.PassId ?? null);
		setPosition(layout?.Position ?? passCount + 1);
	}, [open, layout, passCount]);

	const isNew = layout === null;
	const selectedPass = passIds.find((p) => p.Id === passId) ?? null;
	const canMove = !isNew && passCount > 1;
	const changed = isNew
		? passId !== null
		: passId !== null &&
			(passId !== layout.PassId || position !== layout.Position);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DetailHeader
				title={isNew ? "Add Pass" : "Edit Pass"}
				isSaving={isSaving}
				onClose={onClose}
			/>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (changed && passId !== null) onSave(passId, position);
				}}
			>
				<DialogContent sx={{ p: 2, ...COMPACT_DIALOG_SX }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<DetailSection title="Pass" icon={<FormatListNumberedIcon />}>
							<Grid2 container spacing={1.5} sx={{ pt: 0.5 }}>
								<Grid2 size={3}>
									{canMove ? (
										<TextField
											select
											label="#"
											value={position}
											onChange={(e) => setPosition(Number(e.target.value))}
											fullWidth
											size="small"
											disabled={isSaving}
											slotProps={{ inputLabel: { shrink: true } }}
											sx={EDITABLE_FIELD_SX}
										>
											{Array.from({ length: passCount }, (_, i) => i + 1).map(
												(n) => (
													<MenuItem key={n} value={n} sx={COMPACT_OPTION_SX}>
														{n}
													</MenuItem>
												),
											)}
										</TextField>
									) : (
										<ReadOnlyField
											label="#"
											value={position.toString()}
											numeric
										/>
									)}
								</Grid2>
								<Grid2 size={9}>
									<Autocomplete
										options={passIds}
										value={selectedPass}
										size="small"
										fullWidth
										autoHighlight
										loading={isLoading}
										disabled={isSaving}
										disableClearable={selectedPass !== null}
										getOptionLabel={(pass) => pass.Name}
										isOptionEqualToValue={(option, current) =>
											option.Id === current.Id
										}
										onChange={(_event, pass) => setPassId(pass?.Id ?? null)}
										renderOption={(props, pass) => {
											const { key, ...rest } = props as typeof props & {
												key: string;
											};
											return (
												<li key={pass.Id} {...rest}>
													<Box component="span" sx={COMPACT_OPTION_SX}>
														{pass.Name}
														{pass.PassTypeName && ` - ${pass.PassTypeName}`}
														{pass.PassShapeName && ` (${pass.PassShapeName})`}
													</Box>
												</li>
											);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Pass ID"
												required
												autoFocus
												slotProps={{ inputLabel: { shrink: true } }}
											/>
										)}
										sx={EDITABLE_FIELD_SX}
									/>
								</Grid2>
								<Grid2 size={4}>
									<ReadOnlyField
										label="Type"
										value={selectedPass?.PassTypeName}
									/>
								</Grid2>
								<Grid2 size={4}>
									<ReadOnlyField
										label="Shape"
										value={selectedPass?.PassShapeName}
									/>
								</Grid2>
								<Grid2 size={4}>
									<ReadOnlyField
										label="Sched Tons"
										value={tonnage(selectedPass?.ScheduledTonnage)}
										numeric
									/>
								</Grid2>
							</Grid2>
						</DetailSection>
						{error && <Alert severity="error">{error}</Alert>}
					</Box>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<FooterModalButtons
						isSubmitLoading={isSaving}
						disabled={!changed}
						onHide={onClose}
						buttonLabel="Save"
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};
